import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { X, Moon, Heart, Sparkles, ChevronRight, Check } from 'lucide-react';
import { motion, AnimatePresence, useDragControls } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';
import PremiumPaywall from './PremiumPaywall';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CheckInModal = ({ isOpen, onClose, userId, todaySuggestion, isPremium }) => {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const [data, setData] = useState({
    completed_suggestion: null,
    mood_rating: 3,
    relationship_rating: 3,
    reflection: '',
    gratitude: '',
    tomorrow_intention: ''
  });

  // Check if user is premium on open
  useEffect(() => {
    if (isOpen && !isPremium) {
      setShowPaywall(true);
    } else {
      setShowPaywall(false);
    }
  }, [isOpen, isPremium]);

  const dragControls = useDragControls();
  const totalSteps = 4;

  const moodEmojis = [
    { value: 1, emoji: '😔', label: t('checkin.moodVeryLow') },
    { value: 2, emoji: '😕', label: t('checkin.moodLow') },
    { value: 3, emoji: '😐', label: t('checkin.moodNeutral') },
    { value: 4, emoji: '🙂', label: t('checkin.moodGood') },
    { value: 5, emoji: '😊', label: t('checkin.moodGreat') }
  ];

  const relationshipEmojis = [
    { value: 1, emoji: '💔', label: t('checkin.relVeryLow') },
    { value: 2, emoji: '❤️‍🩹', label: t('checkin.relLow') },
    { value: 3, emoji: '❤️', label: t('checkin.relNeutral') },
    { value: 4, emoji: '💕', label: t('checkin.relGood') },
    { value: 5, emoji: '💖', label: t('checkin.relGreat') }
  ];

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/checkin`, {
        user_id: userId,
        ...data
      });
      
      // Create explicit canvas with transparent background to avoid black square on Android WebView
      const canvas = document.createElement('canvas');
      canvas.style.cssText = 'position:fixed;inset:0;width:100vw;height:100vh;pointer-events:none;z-index:9999;background:transparent;';
      document.body.appendChild(canvas);
      const fire = confetti.create(canvas, { resize: true });
      fire({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 }
      });
      setTimeout(() => canvas.remove(), 3000);
      
      toast.success(t('checkin.saved'));
      onClose();
    } catch (error) {
      console.error('Error saving check-in:', error);
      toast.error(t('checkin.error'));
    } finally {
      setLoading(false);
    }
  };

  const canProceed = () => {
    switch (step) {
      case 0:
        return data.completed_suggestion !== null;
      case 1:
        return data.mood_rating > 0;
      case 2:
        return data.relationship_rating > 0;
      case 3:
        return true; // Optional fields
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (step < totalSteps - 1) {
      setStep(prev => prev + 1);
    } else {
      handleSubmit();
    }
  };

  useEffect(() => {
    if (isOpen) {
      setStep(0);
      setData({
        completed_suggestion: null,
        mood_rating: 3,
        relationship_rating: 3,
        reflection: '',
        gratitude: '',
        tomorrow_intention: ''
      });
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const renderStep = () => {
    switch (step) {
      case 0:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: 'var(--c-surface-card)' }}>
                <Check className="w-8 h-8" style={{ color: 'var(--c-texSec)' }} />
              </div>
              <h3 className="text-lg font-medium mb-2" style={{ color: 'var(--c-texPri)' }}>
                {t('checkin.didYouComplete')}
              </h3>
              {todaySuggestion && (
                <p className="text-sm rounded-full p-3 mt-3" style={{ color: 'var(--c-texSec)', background: 'var(--c-surface-card)' }}>
                  "{todaySuggestion.main_suggestion?.text}"
                </p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setData({ ...data, completed_suggestion: true })}
                className="h-14 rounded-full border-2 font-medium transition-all"
                style={data.completed_suggestion === true
                  ? { background: 'rgba(126,168,108,0.15)', borderColor: '#7EA86C', color: '#7EA86C' }
                  : { borderColor: 'var(--c-border)', color: 'var(--c-texSec)' }
                }
              >
                ✓ {t('checkin.yes')}
              </button>
              <button
                onClick={() => setData({ ...data, completed_suggestion: false })}
                className="h-14 rounded-full border-2 font-medium transition-all"
                style={data.completed_suggestion === false
                  ? { background: 'var(--c-surface-card)', borderColor: 'var(--c-border)', color: 'var(--c-texPri)' }
                  : { borderColor: 'var(--c-border)', color: 'var(--c-texSec)' }
                }
              >
                {t('checkin.no')}
              </button>
            </div>
          </div>
        );

      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(245,158,11,0.1)' }}>
                <Sparkles className="w-8 h-8" style={{ color: '#fbbf24' }} />
              </div>
              <h3 className="text-lg font-medium mb-2" style={{ color: 'var(--c-texPri)' }}>
                {t('checkin.howDoYouFeel')}
              </h3>
              <p className="text-sm" style={{ color: 'var(--c-texTer)' }}>{t('checkin.moodSubtitle')}</p>
            </div>
            <div className="flex justify-center gap-2">
              {moodEmojis.map((mood) => (
                <button
                  key={mood.value}
                  onClick={() => setData({ ...data, mood_rating: mood.value })}
                  className={`w-14 h-14 rounded-2xl text-2xl transition-all ${
                    data.mood_rating === mood.value
                      ? 'scale-110'
                      : ''
                  }`}
                  style={data.mood_rating === mood.value
                    ? { background: 'rgba(245,158,11,0.15)', boxShadow: '0 4px 12px rgba(0,0,0,0.3)' }
                    : { background: 'var(--c-surface-card)' }
                  }
                  title={mood.label}
                >
                  {mood.emoji}
                </button>
              ))}
            </div>
            <p className="text-center text-sm" style={{ color: 'var(--c-texTer)' }}>
              {moodEmojis.find(m => m.value === data.mood_rating)?.label}
            </p>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(244,63,94,0.1)' }}>
                <Heart className="w-8 h-8" style={{ color: '#fb7185' }} />
              </div>
              <h3 className="text-lg font-medium mb-2" style={{ color: 'var(--c-texPri)' }}>
                {t('checkin.howIsRelationship')}
              </h3>
              <p className="text-sm" style={{ color: 'var(--c-texTer)' }}>{t('checkin.relationshipSubtitle')}</p>
            </div>
            <div className="flex justify-center gap-2">
              {relationshipEmojis.map((rel) => (
                <button
                  key={rel.value}
                  onClick={() => setData({ ...data, relationship_rating: rel.value })}
                  className={`w-14 h-14 rounded-2xl text-2xl transition-all ${
                    data.relationship_rating === rel.value
                      ? 'scale-110'
                      : ''
                  }`}
                  style={data.relationship_rating === rel.value
                    ? { background: 'rgba(244,63,94,0.15)', boxShadow: '0 4px 12px rgba(0,0,0,0.3)' }
                    : { background: 'var(--c-surface-card)' }
                  }
                  title={rel.label}
                >
                  {rel.emoji}
                </button>
              ))}
            </div>
            <p className="text-center text-sm" style={{ color: 'var(--c-texTer)' }}>
              {relationshipEmojis.find(r => r.value === data.relationship_rating)?.label}
            </p>
          </div>
        );

      case 3:
        return (
          <div className="space-y-5">
            <div className="text-center mb-4">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(99,102,241,0.1)' }}>
                <Moon className="w-8 h-8" style={{ color: '#818cf8' }} />
              </div>
              <h3 className="text-lg font-medium" style={{ color: 'var(--c-texPri)' }}>
                {t('checkin.reflections')}
              </h3>
              <p className="text-sm" style={{ color: 'var(--c-texTer)' }}>{t('checkin.optionalFields')}</p>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--c-texSec)' }}>
                {t('checkin.reflection')}
              </label>
              <textarea
                value={data.reflection}
                onChange={(e) => setData({ ...data, reflection: e.target.value })}
                placeholder={t('checkin.reflectionPlaceholder')}
                className="w-full h-20 rounded-xl px-4 py-3 resize-none text-sm focus:ring-0"
                style={{ background: 'var(--c-surface-card)', border: '1px solid var(--c-border)', color: 'var(--c-texPri)', outline: 'none' }}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--c-texSec)' }}>
                {t('checkin.gratitude')}
              </label>
              <textarea
                value={data.gratitude}
                onChange={(e) => setData({ ...data, gratitude: e.target.value })}
                placeholder={t('checkin.gratitudePlaceholder')}
                className="w-full h-20 rounded-xl px-4 py-3 resize-none text-sm focus:ring-0"
                style={{ background: 'var(--c-surface-card)', border: '1px solid var(--c-border)', color: 'var(--c-texPri)', outline: 'none' }}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--c-texSec)' }}>
                {t('checkin.intention')}
              </label>
              <input
                type="text"
                value={data.tomorrow_intention}
                onChange={(e) => setData({ ...data, tomorrow_intention: e.target.value })}
                placeholder={t('checkin.intentionPlaceholder')}
                className="w-full h-12 rounded-xl px-4 text-sm focus:ring-0"
                style={{ background: 'var(--c-surface-card)', border: '1px solid var(--c-border)', color: 'var(--c-texPri)', outline: 'none' }}
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          {showPaywall && (
            <PremiumPaywall 
              isOpen={showPaywall}
              onClose={() => {
                setShowPaywall(false);
                onClose();
              }}
              userId={userId}
              feature="checkin"
            />
          )}
          
          {!showPaywall && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[200] flex items-end sm:items-center justify-center"
              onClick={onClose}
            >
              <motion.div
                initial={{ y: '100%', opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: '100%', opacity: 0 }}
                transition={{ type: 'spring', damping: 30, stiffness: 300 }}
                drag="y"
                dragControls={dragControls}
                dragListener={false}
                dragConstraints={{ top: 0, bottom: 0 }}
                dragElastic={{ top: 0, bottom: 0.5 }}
                onDragEnd={(_, info) => { if (info.offset.y > 100) onClose(); }}
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-md rounded-t-2xl sm:rounded-2xl overflow-hidden flex flex-col"
                style={{ background: 'var(--c-surface-elevated)', height: 'calc(100dvh - 24px)', maxHeight: 'calc(100dvh - 24px)' }}
              >
          {/* Drag handle */}
          <div
            className="sm:hidden flex justify-center pt-3 pb-1 cursor-grab active:cursor-grabbing"
            onPointerDown={(e) => dragControls.start(e)}
            style={{ touchAction: 'none' }}
          >
            <div className="w-10 h-1 rounded-full" style={{ background: 'var(--c-border)' }} />
          </div>
          {/* Header */}
          <div className="p-4 flex items-center justify-between" style={{ borderBottom: '1px solid var(--c-border)' }}>
            <div className="flex items-center gap-2">
              <Moon className="w-5 h-5" style={{ color: '#818cf8' }} />
              <h2 className="font-semibold" style={{ color: 'var(--c-texPri)' }}>{t('checkin.title')}</h2>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full flex items-center justify-center transition-colors"
              style={{ background: 'var(--c-surface-card)' }}
            >
              <X className="w-4 h-4" style={{ color: 'var(--c-texSec)' }} />
            </button>
          </div>

          {/* Progress */}
          <div className="px-6 pt-4">
            <div className="flex gap-1">
              {Array.from({ length: totalSteps }).map((_, i) => (
                <div
                  key={i}
                  className="h-1 flex-1 rounded-full transition-colors"
                  style={{ background: i <= step ? 'var(--c-texPri)' : 'var(--c-border)' }}
                />
              ))}
            </div>
            <p className="text-xs mt-2" style={{ color: 'var(--c-texTer)' }}>{step + 1} / {totalSteps}</p>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto flex-1">
            <AnimatePresence initial={false}>
              <motion.div
                key={step}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20, position: 'absolute' }}
                transition={{ duration: 0.15 }}
              >
                {renderStep()}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Footer */}
          <div className="px-4 pt-3 flex gap-3 flex-shrink-0" style={{ borderTop: '1px solid var(--c-border)', paddingBottom: 'max(24px, calc(env(safe-area-inset-bottom) + 16px))' }}>
            {step > 0 && (
              <button
                onClick={() => setStep(prev => prev - 1)}
                className="flex-1 h-14 rounded-xl font-medium transition-colors"
                style={{ border: '1px solid var(--c-border)', color: 'var(--c-texSec)', background: 'transparent' }}
              >
                {t('checkin.back')}
              </button>
            )}
            <button
              onClick={handleNext}
              disabled={!canProceed() || loading}
              className="flex-1 h-14 rounded-xl font-semibold text-base transition-all flex items-center justify-center gap-2"
              style={canProceed() && !loading
                ? { background: 'linear-gradient(to right, #90d960, #6DB33F)', color: '#0a0a0a', boxShadow: '0 0 20px rgba(144, 217, 96, 0.3)' }
                : { background: 'var(--c-surface-card)', color: 'var(--c-texDis)', cursor: 'not-allowed' }
              }
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : step === totalSteps - 1 ? (
                t('checkin.complete')
              ) : (
                <>
                  {t('checkin.next')}
                  <ChevronRight className="w-4 h-4" />
                </>
              )}
            </button>
          </div>
        </motion.div>
      </motion.div>
          )}
        </>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default CheckInModal;
